import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Value } from "../../models/scheme_master";
import useScheme from "../../providers/scheme_provider";
import { SchemeDetailsInputs } from "../../models/scheme_form";
import { useNavigate } from "react-router-dom";
import { black, purple, purpleLight } from "../../../../theme/colors";
import RequiredMark from "../../../../components/required_mark/required_mark";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function SchemeDetails({
  schemeDetails,
  onSubmit: _onSubmit,
}: {
  schemeDetails: SchemeDetailsInputs;
  onSubmit: (data: SchemeDetailsInputs) => void;
}) {
  const navigate = useNavigate();
  const { schemeMaster } = useScheme();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SchemeDetailsInputs>({
    defaultValues: schemeDetails,
  });

  const onSubmit: SubmitHandler<SchemeDetailsInputs> = (data) =>
    _onSubmit(data);

  const schemeNameField = register("schemeName", {
    required: "Enter the scheme name",
  });

  return (
    <Box p={2}>
      <Box mt={2} mb={2}>
        <Typography variant="subtitle1" fontWeight={700} color={black}>
          Scheme Details
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              fullWidth
              label={<>Enter Scheme Name<RequiredMark /></>}
              error={!!errors.schemeName}
              helperText={errors.schemeName?.message}
              {...schemeNameField}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, "");
                schemeNameField.onChange(e);
              }}
            ></TextField>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              select
              fullWidth
              label={<>Type of Government<RequiredMark /></>}
              defaultValue={schemeDetails.governmentTypeId ?? ""}
              error={!!errors.governmentTypeId}
              helperText={errors.governmentTypeId?.message}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    elevation: 0,
                    sx: {
                      mt: 0.5,
                      borderRadius: 2,
                      border: "1px solid #ECECEC",
                      boxShadow: "0px 12px 28px -8px rgba(27, 0, 27, 0.18)",
                    },
                  },
                },
              }}
              {...register("governmentTypeId", {
                required: "Select the type of govt.",
              })}
            >
              <MenuItem value="" disabled>
                Select type of government
              </MenuItem>
              {schemeMaster?.schemeGovernment?.map((govt) => (
                <MenuItem key={govt.id} value={govt.id}>{govt.value}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name="typeOfCategory"
              control={control}
              rules={{ required: "Select category" }}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  fullWidth
                  multiple
                  options={schemeMaster?.schemeCategory || []}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.value}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(_, value) => onChange(value)}
                  value={value}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.value}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.typeOfCategory}
                      helperText={errors.typeOfCategory?.message}
                      label={<>Type of Category<RequiredMark /></>}
                      fullWidth
                      placeholder="Select type of categories"
                    />
                  )}
                />
              )}
            ></Controller>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Controller
              name="providerId"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  fullWidth
                  options={schemeMaster?.schemeProviders || []}
                  getOptionLabel={(option) => option.value}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={
                    schemeMaster?.schemeProviders?.find((p) => p.id === value) ||
                    null
                  }
                  onChange={(_, option) => onChange(option?.id ?? null)}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.logo && (
                        <Box
                          component="img"
                          src={option.logo}
                          alt=""
                          sx={{ width: 20, height: 20, objectFit: "contain", mr: 1 }}
                        />
                      )}
                      {option.value}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Provider (Optional)" fullWidth />
                  )}
                />
              )}
            />
          </Grid>
          {/* <Grid item md={12}>
            <FormControlLabel
              label="Mark scheme as top scheme"
              control={<Checkbox {...register("topScheme")} />}
            />
          </Grid> */}
        </Grid>

        <Box mt={2} mb={2}>
          <Divider />
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Button
            variant="outlined"
            sx={{
              pl: 6,
              pr: 6,
              borderColor: purple,
              color: purple,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { borderColor: purple, bgcolor: purpleLight },
            }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            sx={{
              pl: 6,
              pr: 6,
              bgcolor: purple,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#7A2074" },
            }}
          >
            Next
          </Button>
        </Box>
      </form>
    </Box>
  );
}
